// AdminTodo Component - Checklist/To-Do management
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { 
  ListTodo, Plus, RefreshCw, Loader2, Circle, CheckCircle2, PlayCircle,
  Download, Upload
} from 'lucide-react';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTodo({ checklist: propChecklist, adminKey, onRefresh }) {
  // Always call useAdmin hook (required by React rules)
  const contextValues = useAdmin() || {};
  
  // Use props if provided, otherwise fallback to context
  const checklist = propChecklist || contextValues.checklist || [];
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  });
  const fetchChecklist = onRefresh || contextValues.fetchChecklist || (() => {});
  const language = contextValues.language || 'fr';
  
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'P2', category: 'feature' });

  const initChecklist = async () => {
    setChecklistLoading(true);
    try {
      await api.post('/admin/checklist/init');
      toast.success(language === 'fr' ? 'Checklist initialisée!' : 'Checklist initialized!');
      fetchChecklist();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setChecklistLoading(false);
    }
  };

  const addChecklistItem = async () => {
    if (!newTask.title.trim()) return;
    setChecklistLoading(true);
    try {
      await api.post('/admin/checklist', newTask);
      toast.success(language === 'fr' ? 'Tâche ajoutée!' : 'Task added!');
      setNewTask({ title: '', description: '', priority: 'P2', category: 'feature' });
      fetchChecklist();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setChecklistLoading(false);
    }
  };

  const updateChecklistStatus = async (itemId, newStatus) => {
    try {
      await api.put(`/admin/checklist/${itemId}`, { status: newStatus });
      fetchChecklist();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const exportData = () => {
    const dataStr = JSON.stringify(checklist, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `checklist_export_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  const priorityColors = {
    P0: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: '🔴 P0 - Critique' },
    P1: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', label: '🟠 P1 - Haute' },
    P2: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: '🟡 P2 - Moyenne' },
    P3: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', label: '🟢 P3 - Basse' }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
              <ListTodo className="h-5 w-5 text-white" />
            </div>
            To Do
          </h2>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' ? 'Checklist des tâches du projet PayHub Québec+' : 'PayHub Québec+ project task checklist'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={exportData}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        {checklist.length === 0 && (
          <Button onClick={initChecklist} disabled={checklistLoading} className="bg-violet-600 hover:bg-violet-700">
            {checklistLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {language === 'fr' ? 'Initialiser la checklist' : 'Initialize checklist'}
          </Button>
        )}
      </div>

      {/* Add New Task */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input 
                placeholder={language === 'fr' ? 'Nouvelle tâche...' : 'New task...'}
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
              />
            </div>
            <select 
              className="h-10 px-3 rounded-md border border-input bg-background"
              value={newTask.priority}
              onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
            >
              <option value="P0">🔴 P0</option>
              <option value="P1">🟠 P1</option>
              <option value="P2">🟡 P2</option>
              <option value="P3">🟢 P3</option>
            </select>
            <select 
              className="h-10 px-3 rounded-md border border-input bg-background"
              value={newTask.category}
              onChange={(e) => setNewTask({...newTask, category: e.target.value})}
            >
              <option value="feature">Feature</option>
              <option value="bug">Bug</option>
              <option value="refactor">Refactor</option>
              <option value="integration">Integration</option>
              <option value="other">{language === 'fr' ? 'Autre' : 'Other'}</option>
            </select>
            <Button onClick={addChecklistItem} disabled={checklistLoading || !newTask.title.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Ajouter' : 'Add'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-muted/30">
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-violet-600">{checklist.filter(t => t.status === 'completed').length}</p>
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Complétées' : 'Completed'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{checklist.filter(t => t.status === 'in_progress').length}</p>
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'En cours' : 'In Progress'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">{checklist.filter(t => t.status === 'pending').length}</p>
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'En attente' : 'Pending'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{checklist.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Checklist Items by Priority */}
      {['P0', 'P1', 'P2', 'P3'].map(priority => {
        const priorityItems = checklist.filter(item => item.priority === priority);
        if (priorityItems.length === 0) return null;
        const pc = priorityColors[priority];
        
        return (
          <Card key={priority} className={`border-l-4 ${pc.border}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm ${pc.bg} ${pc.text}`}>{pc.label}</span>
                <span className="text-muted-foreground text-sm font-normal">
                  ({priorityItems.filter(i => i.status === 'completed').length}/{priorityItems.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {priorityItems.map(item => (
                <div 
                  key={item.item_id} 
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    item.status === 'completed' 
                      ? 'bg-green-50 border-green-200 opacity-70' 
                      : item.status === 'in_progress'
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-background hover:bg-muted/50'
                  }`}
                >
                  <button 
                    onClick={() => updateChecklistStatus(
                      item.item_id, 
                      item.status === 'pending' ? 'in_progress' : item.status === 'in_progress' ? 'completed' : 'pending'
                    )}
                    className="flex-shrink-0"
                  >
                    {item.status === 'completed' ? (
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    ) : item.status === 'in_progress' ? (
                      <PlayCircle className="h-6 w-6 text-blue-600" />
                    ) : (
                      <Circle className="h-6 w-6 text-muted-foreground hover:text-violet-600" />
                    )}
                  </button>
                  
                  <div className="flex-1">
                    <p className={`font-medium ${item.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                      {item.title}
                    </p>
                    {item.description && (
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    )}
                    {item.completed_at && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {language === 'fr' ? 'Complété le' : 'Completed on'} {new Date(item.completed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.category === 'feature' ? 'bg-violet-100 text-violet-700' :
                    item.category === 'bug' ? 'bg-red-100 text-red-700' :
                    item.category === 'refactor' ? 'bg-blue-100 text-blue-700' :
                    item.category === 'integration' ? 'bg-teal-100 text-teal-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {item.category}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}

      {checklist.length === 0 && (
        <Card className="border-2 border-dashed">
          <CardContent className="py-12 text-center">
            <ListTodo className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              {language === 'fr' ? 'Aucune tâche dans la checklist' : 'No tasks in the checklist'}
            </p>
            <Button onClick={initChecklist} disabled={checklistLoading}>
              {checklistLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {language === 'fr' ? 'Initialiser avec les tâches par défaut' : 'Initialize with default tasks'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
